import { type ClassValue, clsx } from 'clsx';

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

// de-DE has the same separators as es-ES (1.284,50) but also groups
// four-digit numbers, as the Advia design system asks.
const DISPLAY_LOCALE = 'de-DE';

/** Fixed decimals with European separators, for display only. */
export function formatDecimal(value: number, decimals: number): string {
  return value.toLocaleString(DISPLAY_LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function formatNumber(
  value: number | null | undefined,
  format: string,
  decimals: number = 2
): string {
  // `== null` on purpose: callers reading values out of chart libraries get
  // `undefined` as often as `null`, and every branch below would throw on it.
  if (value == null) return '-';

  switch (format) {
    case 'percentage':
      return `${formatDecimal(value, decimals)}%`;
    case 'euro':
      return formatCompactEuro(value, decimals);
    case 'k-euro':
      return `${formatDecimal(value / 1000, decimals)} K€`;
    case 'dollar':
      return `$${formatDecimal(value, decimals)}`;
    case 'decimal':
      return formatDecimal(value, decimals);
    case 'number':
    default:
      return value.toLocaleString(DISPLAY_LOCALE, {
        minimumFractionDigits: 0,
        maximumFractionDigits: decimals
      });
  }
}

function formatCompactEuro(value: number, decimals: number = 2): string {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 1_000_000_000) {
    return `${sign}${formatDecimal(absValue / 1_000_000_000, decimals)} B€`;
  } else if (absValue >= 1_000_000) {
    return `${sign}${formatDecimal(absValue / 1_000_000, decimals)} M€`;
  } else if (absValue >= 1_000) {
    return `${sign}${formatDecimal(absValue / 1_000, decimals)} K€`;
  } else {
    return `${sign}${formatDecimal(absValue, decimals)}€`;
  }
}

export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

export function getDaysRemaining(endDate: string): number {
  const end = new Date(endDate);
  const now = new Date();
  const diff = end.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export function formatRelativeTime(isoDate: string): string {
  const now = new Date();
  const date = new Date(isoDate);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}
